// Seizure's electrode in the inspector (spec §15, Electrode: channel trace). A click on a drawn site
// selects it. A model sensor shows its channel's amplitude now over its whole trace, the region it
// hears most (each region's share of the channel's variance over the run, through the head model), the
// tissue a depth contact sits in, and the forward model that made it. A real patient's contact shows
// their own recording there beside the model fitted to it, the region it sits in, and whether the
// clinicians marked it in the onset zone. Spec's Impedance isn't shown: no run models one.

import { useMemo, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { mainSlot } from '../app/scene.ts';
import { openedSlot } from '../app/sims.ts';
import type { Catalogue } from '../data/catalogue.ts';
import { channelGroups, type DynamicsSim } from '../data/sims/dynamics.ts';
import { asegLabel, channelSources, column, mean, parseSiteId, rmsBefore, sensorInfo, type SensorKind } from '../data/sims/electrodes.ts';
import { liveliestGroup, modalityOf, openComparison } from '../data/sims/comparison.ts';
import { defaultSource } from '../data/sims/files.ts';
import { duration, sampleAt, type TimeAxis } from '../data/sims/time.ts';
import { formatValue } from '../ui/chart.ts';
import { Disclosure } from '../ui/Disclosure.tsx';
import { Button, IconButton } from '../ui/primitives.tsx';
import { Sparkline } from '../ui/Sparkline.tsx';
import { useLoaded } from '../ui/hooks.ts';
import { mainRun } from './ElectrodeSites.tsx';
import { regionName, UNIT } from './PatientTraces.tsx';
import { groupName } from './Traces.tsx';

/** Where `t` falls on an axis, 0–1, for a sparkline's time mark. */
const along = (a: TimeAxis, t: number): number | null => {
  const d = duration(a);
  return d > 0 ? Math.min(1, Math.max(0, (t - a.t0) / d)) : null;
};

/** A potential in µV, in mV from a millivolt (a depth contact in the seizure), as the traces' strip reads them. */
const potential = (uv: number) => (Math.abs(uv) >= 1000 ? `${formatValue(uv / 1000)} mV` : `${formatValue(uv)} µV`);

const pct = (share: number) => `${Math.round(100 * share)} %`;

/** A patient contact's sparklines: samples over the scene's clock, two a pixel. */
const SAMPLES = 240;

/** A run's parcel by its index or source name: its name in the scene's pack, and its structure where the pack has it. */
export function parcelRegion(sim: DynamicsSim, catalogue: Catalogue | null, p: number): { id: string | null; name: string } {
  const parcel = sim.parcels[p];
  const id = parcel?.structure && catalogue?.byId.has(parcel.structure) ? parcel.structure : null;
  return { id, name: regionName(sim, catalogue, parcel?.source_name ?? '') || `Region ${p + 1}` };
}

/** A region a row names: a link that selects it where the scene's pack has it. */
export function Region({ id, name }: { id: string | null; name: string }) {
  const select = useAtlas((s) => s.select);
  const hover = useAtlas((s) => s.hover);
  if (!id) return <>{name}</>;
  return (
    <button type="button" className="insp-link" onClick={() => select(id)} onMouseEnter={() => hover(id, 'ui')} onMouseLeave={() => hover(null, 'ui')}>
      {name}
    </button>
  );
}

function Shell({ eyebrow, title, actions, children }: { eyebrow: string; title: string; actions?: ReactNode; children: ReactNode }) {
  const open = useAtlas((s) => s.panels.right);
  const select = useAtlas((s) => s.select);
  return (
    <aside className={`panel right inspector ${open ? '' : 'collapsed'}`} aria-label="Inspector" data-inset="right">
      <div className="panel-scroll">
        <header className="insp-head">
          <div className="insp-eyebrow caps">{eyebrow}</div>
          <h2 className="insp-name">{title}</h2>
          <IconButton className="insp-close" size="sm" icon={X} label="Clear selection" keys={['Esc']} onClick={() => select(null)} />
        </header>
        {actions ? <div className="insp-actions">{actions}</div> : null}
        {children}
      </div>
    </aside>
  );
}

/** The scene's model run's sensor: its channel, the regions it hears, and its forward model. */
function SensorPanel({ sim, path, kind, name }: { sim: DynamicsSim; path: string; kind: SensorKind; name: string }) {
  const time = useAtlas((s) => s.time);
  const catalogue = useAtlas((s) => s.catalogue);
  const montage = useAtlas((s) => s.traceView.montage);
  const wanted = useAtlas((s) => s.traceView.group);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const names = sim.channels(kind);
  const ch = names.indexOf(name);
  const loaded = useLoaded(async () => {
    if (ch < 0) return null;
    const [sens, info] = await Promise.all([sim.sensors(kind), sensorInfo(defaultSource(), path, sim, kind, name)]);
    const y = column(sens.data, sens.channels.length, ch);
    const centre = mean(y);
    const sources = await channelSources(sim, kind, ch, y).catch(() => null);
    return { y, centre, toUv: sens.units === 'V' ? 1e6 : 1, axis: sens.axis, info, sources };
  }, [sim, path, kind, ch]);
  const r = loaded.value;
  const trace = useMemo(() => (r ? r.y.map((v) => (v - r.centre) * r.toUv) : null), [r]);
  const groups = useMemo(() => channelGroups(kind, names), [kind, names]);
  if (ch < 0) return null;
  const group = groups.find((g) => g.channels.includes(ch));
  // The strip shows depth contacts first where the run has both (Traces.tsx), one group at a time.
  const kinds = (['seeg', 'eeg'] as SensorKind[]).filter((k) => sim.has(k) && sim.channels(k).length);
  const shown = montage && kinds.includes(montage) ? montage : kinds[0];
  const inStrip = shown === kind && (groups.find((g) => g.id === wanted) ?? groups[0])?.id === group?.id;
  const rms = r ? rmsBefore(r.y, r.axis, time, r.centre) : null;
  const top = r?.sources?.slice(0, 3) ?? [];
  const hears = top[0] ? parcelRegion(sim, catalogue, top[0].parcel) : null;
  const tissue = r?.info.aseg != null ? asegLabel(r.info.aseg) : null;
  const tissueParcel = tissue?.parcel ? sim.parcels.findIndex((p) => p.source_name === tissue.parcel) : -1;
  const at = tissueParcel >= 0 ? parcelRegion(sim, catalogue, tissueParcel) : tissue ? { id: null, name: tissue.name } : null;
  const eyebrow = [kind === 'eeg' ? 'Scalp EEG' : 'SEEG', group ? groupName(kind, group.label) : null].filter(Boolean).join(' · ');
  const data = kind === 'eeg' ? 'Simulated on a template head' : r?.info.byHand ? 'Simulated at a contact placed by hand' : 'Simulated';
  const how = [r?.info.model, r?.info.placement].filter(Boolean).map((x) => `${x!.replace(/\.$/, '')}.`);
  return (
    <Shell
      eyebrow={eyebrow}
      title={name}
      actions={
        group && !inStrip ? (
          <Button title="Show its group of channels in the traces below" onClick={() => setTraceView({ montage: kind, group: group.id })}>
            Show trace
          </Button>
        ) : null
      }
    >
      {r?.info.reference ? <p className="insp-desc">{`Referenced to the ${r.info.reference.replace(/\.$/, '')}.`}</p> : null}
      {r && trace ? (
        <dl className="insp-facts insp-live-rows insp-sim-rows">
          <div className="insp-live-row">
            <dt>Amplitude</dt>
            <dd className="num" title="RMS about its mean over the run, in the second up to now; the trace is its whole run about that mean">
              {rms === null ? '–' : potential(rms * r.toUv)}
            </dd>
            <Sparkline y={trace} at={along(r.axis, time)} />
          </div>
          {hears && top[0] ? (
            <div className="insp-live-row">
              <dt>Hears</dt>
              <dd
                className="insp-wide"
                title={`Each region’s share of this channel’s variance over the run, through the head model: ${top.map((x) => `${parcelRegion(sim, catalogue, x.parcel).name} ${pct(x.share)}`).join(', ')}`}
              >
                <Region id={hears.id} name={hears.name} />
                {`, ${pct(top[0].share)}`}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      <dl className="insp-facts">
        {at ? (
          <>
            <dt title="The tissue at the contact: its nearest voxel’s label in the template’s FreeSurfer segmentation">In</dt>
            <dd>
              <Region id={at.id} name={at.name} />
            </dd>
          </>
        ) : null}
        <dt>Data</dt>
        <dd>{data}</dd>
      </dl>
      {how.length || r?.sources ? (
        <Disclosure title="How this was made">
          {how.map((x) => (
            <p key={x}>{x}</p>
          ))}
          {r?.sources ? (
            <p className="insp-measured">The run’s sensors are its regions’ signals through the head model’s gain, so a channel’s variance over the run splits exactly between the regions: Hears is the largest share.</p>
          ) : null}
        </Disclosure>
      ) : null}
    </Shell>
  );
}

/** A real patient's contact: their recording there beside the model fitted to it, where it sits, and the clinicians' marks. */
function ContactPanel({ sim, name }: { sim: DynamicsSim; name: string }) {
  const time = useAtlas((s) => s.time);
  const catalogue = useAtlas((s) => s.catalogue);
  const wanted = useAtlas((s) => s.traceView.group);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const modality = useAtlas((s) => modalityOf(mainSlot(s)?.annotation?.label));
  const loaded = useLoaded(() => openComparison(sim), [sim]);
  const c = loaded.value;
  const k = c ? c.contacts.findIndex((x) => x.name === name) : -1;
  // Both over the scene's clock, the model's, the recording at the scene time less the onset as the strip
  // lines them up: one time mark for the two.
  const series = useMemo(() => {
    if (!c || k < 0) return null;
    const C = c.contacts.length;
    const a = c.model.axis;
    const n = Math.max(2, Math.min(SAMPLES, a.frames));
    const times = Float64Array.from({ length: n }, (_, i) => a.t0 + (i / (n - 1)) * duration(a));
    return {
      observed: Float32Array.from(times, (t) => sampleAt(c.observed.data, C, k, c.observed.axis, t - c.onset)),
      model: Float32Array.from(times, (t) => sampleAt(c.model.data, C, k, a, t)),
    };
  }, [c, k]);
  const contact = c?.contacts[k];
  if (!c || !contact || !series) return null;
  const C = c.contacts.length;
  const recorded = sampleAt(c.observed.data, C, k, c.observed.axis, time - c.onset);
  const modelled = sampleAt(c.model.data, C, k, c.model.axis, time);
  const unit = (u: string) => (UNIT[u] ? ` ${UNIT[u]}` : '');
  const parcel = contact.parcel ? sim.parcels.findIndex((p) => p.source_name === contact.parcel) : -1;
  const region = parcel >= 0 ? parcelRegion(sim, catalogue, parcel) : contact.parcel ? { id: null, name: regionName(sim, catalogue, contact.parcel) } : null;
  const group = c.groups.find((g) => g.contacts.includes(k));
  const shown = c.groups.find((g) => g.id === wanted) ?? liveliestGroup(c);
  const marks = contact.soz
    ? `In the seizure onset zone the clinicians marked${contact.resected ? ', and resected at surgery' : ''}.`
    : contact.resected
      ? 'Resected at surgery, outside the onset zone the clinicians marked.'
      : null;
  const o = c.observation;
  const how = [
    o.observed?.feature ? `The recording: ${o.observed.feature}${o.observed.seizure ? `, in ${o.observed.seizure}` : ''}${o.observed.source ? ` (${o.observed.source})` : ''}.` : null,
    o.model ? `The model at the contact: ${o.model}${o.gain ? `, its gain ${o.gain}` : ''}.` : null,
  ].filter((x): x is string => !!x);
  return (
    <Shell
      eyebrow={[modality ?? 'Contact', c.patient].filter(Boolean).join(' · ')}
      title={contact.label}
      actions={
        group && shown?.id !== group.id ? (
          <Button title="Show its electrode’s contacts in the traces below" onClick={() => setTraceView({ group: group.id })}>
            Show trace
          </Button>
        ) : null
      }
    >
      {marks ? <p className="insp-desc">{marks}</p> : null}
      <dl className="insp-facts insp-live-rows insp-sim-rows">
        <div className="insp-live-row">
          <dt>Recorded</dt>
          <dd className="num" title={`Their recording at this contact${o.observed?.feature ? `: ${o.observed.feature}` : ''}, lined up with the model at the onset as the traces are`}>
            {`${formatValue(recorded, 2)}${unit(c.observed.units)}`}
          </dd>
          <Sparkline y={series.observed} at={along(c.model.axis, time)} />
        </div>
        <div className="insp-live-row">
          <dt>Model</dt>
          <dd className="num" title={`The model fitted to them, at this contact${o.model ? `: ${o.model}` : ''}`}>
            {`${formatValue(modelled, 2)}${unit(c.model.units)}`}
          </dd>
          <Sparkline y={series.model} at={along(c.model.axis, time)} />
        </div>
      </dl>
      <dl className="insp-facts">
        {region ? (
          <>
            <dt title="The model region the contact pair’s midpoint sits in">In</dt>
            <dd>
              <Region id={region.id} name={region.name} />
            </dd>
          </>
        ) : null}
        <dt>Data</dt>
        <dd>Recorded in this patient</dd>
      </dl>
      {how.length ? (
        <Disclosure title="How this was made">
          {how.map((x) => (
            <p key={x}>{x}</p>
          ))}
        </Disclosure>
      ) : null}
    </Shell>
  );
}

/** The selected electrode site (`electrode:<kind>:<channel>`) on the scene's dynamics run. */
export function ElectrodePanel({ id }: { id: string }) {
  const path = useAtlas((s) => mainRun(s)?.path ?? null);
  const site = parseSiteId(id);
  const o = path ? openedSlot('main') : undefined;
  const sim = o?.format === 'dynamics' && o.path === path ? o.sim : null;
  if (!site || !sim || !path) return null;
  return site.kind === 'contacts' ? <ContactPanel sim={sim} name={site.name} /> : <SensorPanel sim={sim} path={path} kind={site.kind} name={site.name} />;
}
